import { useState, useEffect } from 'react';
import { logError } from '../../features/shared/error/logError';

export function useApi<T>() {
	const [data, setData] = useState<T | null>(null);
	const [loading, setLoading] = useState(false);
	const [error, setError] = useState<
		Error | { status: number; message: string } | null
	>(null);

	const handleApiData = async (options: {
		url: string;
		method: 'GET' | 'POST' | 'PUT';
		body?: T;
		apiKey?: string;
		apiVersion?: string;
		headers?: RequestInit['headers'];
	}) => {
		const { url, method, body, apiKey, apiVersion, headers } = options;

		try {
			setLoading(true);

			const apiOptions: RequestInit = {
				method,
				headers: headers || {
					'Content-Type': 'application/json',
					'Api-Key': `${apiKey || process.env.DAP_API_KEY}`,
					'Api-Version': `${apiVersion || '100'}`,
				},
			};

			if (body && (method === 'POST' || method === 'PUT')) {
				apiOptions.body = JSON.stringify(body);
			}

			const response = await fetch(url, apiOptions);

			if (!response.ok) {
				setError({ status: response.status, message: response.statusText });
				return null;
			}

			const result = await response.json();
			setData(result);
			return result;
		} catch (e) {
			const customError =
				e instanceof Error ? e.message : 'An unexpected error occurred';
			setError({ status: 500, message: customError });
			logError(e);
		} finally {
			setLoading(false);
		}
		return null;
	};

	const useGetApi = (options: {
		url: string;
		apiKey?: string;
		apiVersion?: string;
		headers?: RequestInit['headers'];
	}) => {
		const { url, apiKey, apiVersion, headers } = options;

		useEffect(() => {
			handleApiData({ url, method: 'GET', apiKey, apiVersion, headers });
		}, [url]);

		return {
			data,
			loading,
			error,
			refetch: () => handleApiData({ url, method: 'GET', apiKey, apiVersion }),
		};
	};

	return { data, loading, error, handleApiData, useGetApi };
}

export default useApi;
